
import { computed, defineComponent, onMounted, reactive } from 'vue'

// masks
import { TIME_MASK } from '@/core/constants/masks.constants'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'
import RadioGroup from '@/common/components/UI/RadioGroup/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import infoIcon from '@/assets/icons/info.svg'

// validations
import useVuelidate from '@vuelidate/core'
import { integer, required, requiredIf, decimal, minLength } from '@vuelidate/validators'
import { carriers } from '@/core/realisations/carriers.realisation'
import { DataState } from '@/core/classes/DataState.class'
import { CarrierT } from '@/core/types/carriers.types'

// constants
import { FTL_ORDER_PAYMENT_RATE_TYPES } from '@/core/constants/ftl_types.constants'
import { PropType } from 'vue'
import {
    AdditionalServicesFTLI,
    AdditionalServicesModalFtl,
} from '@/core/types/additional_services.types'

export default defineComponent({
    components: {
        Modal,
        TheInput,
        Checkbox,
        RadioGroup,
        TheSelect,
        infoIcon,
    },
    emits: ['cancel', 'submitted'],
    props: {
        isLoading: {
            type: Boolean,
            default: false,
        },
        ftlType: {
            type: Number,
            default: null,
        },
        isNeedCar: {
            type: Boolean,
            default: false,
        },
        senderCity: {
            type: String,
            default: null,
        },
        statusCode: {
            type: String,
            default: null,
        },
        additionalServices: {
            type: Array as PropType<AdditionalServicesFTLI[]>,
            default: null,
        },
    },
    setup(props, { emit }) {
        const state = reactive({
            is_loading: false,
        })

        const delivery_state = reactive({
            delivery_date: null as string | null,
            delivery_time: '00:00',
            received_by: null as string | null,
            //
            auto_count: null as number | null,
            auto_duration: null as number | null,
            //
            has_downtime: false,
            downtime: {
                type: 1,
                hours: null as string | number | null,
                cost_price: null as string | number | null,
            },
            //
            additional_services: [] as AdditionalServicesModalFtl[],
        })

        const data_state = reactive(new DataState<CarrierT>({}))

        const isFTLTypeOne = computed(() => props.ftlType === 1)
        const isFTLTypeTwo = () => props.ftlType === 2

        const isOnTheWay = computed(() => props.statusCode === 'on_the_way')

        const state_form$ = useVuelidate(
            {
                delivery_date: { required },
                delivery_time: { required, minLength: minLength(5) },
                received_by: { required },
                auto_count: {
                    required: requiredIf(() => isFTLTypeTwo() && props.isNeedCar),
                    integer,
                },
                auto_duration: {
                    required: requiredIf(() => isFTLTypeTwo() && props.isNeedCar),
                    decimal,
                },
                downtime: {
                    hours: {
                        required: requiredIf(() => delivery_state.has_downtime && isOnTheWay.value),
                    },
                    cost_price: {
                        required: requiredIf(() => delivery_state.has_downtime && isOnTheWay.value),
                    },
                },
            },
            delivery_state
        )

        const loadCarriers = async (page: number) => {
            data_state.pagination.current_page = page

            try {
                const params = { page, getAllCarriers: 1, limit: 9999, statusId: 2 }

                data_state.is_loading = true
                const data = await carriers.getList(params)

                data_state.list = data.data
                data_state.pagination.total_pages = data.meta.last_page

                data_state.is_failed = false
                data_state.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.failure.catchError(error)

                if (!data_state.failure.error_state.is_cancel) {
                    data_state.is_failed = true
                }
            } finally {
                if (!data_state.failure.error_state.is_cancel) {
                    data_state.is_loading = false
                }
            }
        }

        const loadMoreCarriers = async (page: number) => {
            if (page > data_state.pagination.total_pages) return
            data_state.pagination.current_page = page

            try {
                const params = { page, getAllCarriers: 1, limit: 9999, statusId: 2 }

                data_state.is_loading = true

                const response = await carriers.getList(params)
                data_state.list = data_state.list.concat(response.data)

                data_state.pagination.total_pages = response.meta.last_page

                data_state.is_failed = false
                data_state.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.failure.catchError(error)

                if (!data_state.failure.error_state.is_cancel) {
                    data_state.is_failed = true
                }
            } finally {
                if (!data_state.failure.error_state.is_cancel) {
                    data_state.is_loading = false
                }
            }
        }

        const emitCancel = () => {
            emit('cancel')
        }

        const emitSubmit = () => {
            const additional_services = delivery_state.additional_services.map((service) => {
                return {
                    id: service.id,
                    carrier_id: service.carrier_id,
                    cost_per_hour: service.cost_per_hour,
                    duration: service.duration,
                    value: service.value,
                    cost_total: service.cost_total,
                }
            })

            if (
                !additional_services.every(
                    (service) =>
                        service.carrier_id &&
                        (service.cost_per_hour || service.cost_per_hour === 0) &&
                        (service.duration || service.duration === '0') &&
                        (service.value || service.value === '0') &&
                        (service.cost_total || service.cost_total === 0)
                )
            ) {
                return
            }

            const payload = {
                ...delivery_state,
                additional_services,
            }

            emit('submitted', payload)
        }

        const emitSubmitOnTheWay = () => {
            const additional_services = delivery_state.additional_services.map((service) => {
                return {
                    id: service.id,
                    carrier_id: service.carrier_id,
                    cost_per_hour: service.cost_per_hour,
                    duration: service.duration,
                    value: service.value,
                    cost_total: service.cost_total,
                }
            })

            if (
                !additional_services.every(
                    (service) =>
                        service.carrier_id &&
                        (service.cost_per_hour || service.cost_per_hour === 0) &&
                        (service.duration || service.duration === '0') &&
                        (service.value || service.value === '0') &&
                        (service.cost_total || service.cost_total === 0)
                )
            ) {
                return
            }

            const payload = {
                delivery_date: delivery_state.delivery_date,
                delivery_time: delivery_state.delivery_time,
                received_by: delivery_state.received_by,
                auto_count: delivery_state.auto_count,
                auto_duration: delivery_state.auto_duration,
                has_downtime: delivery_state.has_downtime,
                downtime: {
                    type: delivery_state.downtime.type,
                    hours: Number(delivery_state.downtime.hours),
                    cost_price: Number(delivery_state.downtime.cost_price),
                },
                additional_services,
            }

            emit('submitted', payload)
        }

        const manageSubmitted = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            if (isOnTheWay.value) emitSubmitOnTheWay()
            else emitSubmit()
        }

        const isHaveDuration = (type_id: number) => {
            return type_id !== 1 && type_id !== 3 && type_id !== 4 && type_id !== 5 && type_id !== 6
        }

        const isHaveValue = (type_id: number) => {
            return type_id !== 1
        }

        const getPlaceholder = (placeholder: string) => {
            if (delivery_state.downtime) {
                return delivery_state.downtime.type === 2 ? `${placeholder} (за час)` : placeholder
            }

            return placeholder
        }

        const isModalLoading = computed(() => {
            return props.isLoading || data_state.is_loading
        })

        const setAdditionalServices = () => {
            if (!props.additionalServices || !props.additionalServices.length) return

            delivery_state.additional_services = props.additionalServices.map((service) => {
                return {
                    id: service.id,
                    carrier_id: service.carrier_id,
                    cost_per_hour: service.cost_per_hour,
                    duration: service.duration,
                    value: service.value,
                    cost_total: service.cost_total,
                    //
                    isSpark: false,
                    type_of_stock: service.type_of_stock,
                    city_name: service.city_name,
                    type_name: service.type_name,
                }
            })
        }

        const onSparkChange = (service: AdditionalServicesModalFtl) => {
            if (service.isSpark) {
                service.carrier_id = 287
                service.cost_per_hour = 0
                service.cost_total = 0
            }
        }

        onMounted(async () => {
            await loadCarriers(1)
            setAdditionalServices()
        })

        return {
            state,
            data_state,
            delivery_state,
            state_form$,
            emitCancel,
            manageSubmitted,
            loadCarriers,
            loadMoreCarriers,
            isHaveDuration,
            isHaveValue,
            isFTLTypeTwo,
            isFTLTypeOne,
            getPlaceholder,
            onSparkChange,
            //
            isModalLoading,
            isOnTheWay,
            //
            TIME_MASK,
            FTL_ORDER_PAYMENT_RATE_TYPES,
        }
    },
})
